import { getProductBundles, getPublicProductBundles } from 'pricing-pages-lib/api/ProductBundlesApi';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { createProductConfig, getProductConfigurationsWithoutProductLine } from 'pricing-pages-lib/utils/productConfigUtils';
import { getCurrentSkuIdForProduct, getProductForSkuId } from 'pricing-pages-lib/utils/productsUtils';
import { useCallback } from 'react';
import { shallowEqual } from 'react-redux';
import { useAppSelector } from '../../../reducers';
import { isAppAuthSelector } from '../../../selectors/AuthSelectors';
import { activeCurrencySelector } from '../../../selectors/CurrencySelectors';
import { gatesSelector, productDataSelector, productInventorySelector } from '../../../selectors/SelectorUtils';
import { productNamesByProductLevelMapSelector } from '../../../selectors/TrackingSelectors';
export const useSetProductLevel = ({
  productConfigurations,
  setProductConfigurations,
  productLine,
  selectedProductLevel,
  ownedProductLevel,
  screen,
  setIsLoading,
  trackingSource
}) => {
  const currencyCode = useAppSelector(activeCurrencySelector);
  const isAppAuth = useAppSelector(isAppAuthSelector);
  const productData = useAppSelector(productDataSelector);
  const gates = useAppSelector(gatesSelector);
  const productInventory = useAppSelector(productInventorySelector);
  const trackingProductNamesByProductLevelMap = useAppSelector(state => productNamesByProductLevelMapSelector(state, {
    productLine
  }), shallowEqual);
  const setProductLevel = useCallback(newProductLevel => {
    if (!newProductLevel) {
      if (selectedProductLevel === ownedProductLevel) {
        return;
      } else {
        newProductLevel = ownedProductLevel;
      }
    }
    if (newProductLevel) {
      getUsageTracker().track('planBuilderInteraction', {
        component: 'HubPlanPickerList',
        action: 'clicked product level selection button',
        productName: trackingProductNamesByProductLevelMap[newProductLevel],
        skuId: getCurrentSkuIdForProduct({
          productData
        }, {
          productLine,
          productLevel: newProductLevel
        }),
        screen,
        currency: currencyCode,
        trackingSource
      });
    }
    const filteredConfigs = getProductConfigurationsWithoutProductLine({
      productData,
      productInventory,
      gates
    }, {
      productConfigurations,
      productLine
    });
    const filteredApiNames = filteredConfigs.map(({
      skuId
    }) => {
      const product = getProductForSkuId({
        productData
      }, {
        skuId
      });
      return product && product.apiName;
    });
    const originalApiNames = filteredApiNames;
    if (newProductLevel) {
      const newSkuId = getCurrentSkuIdForProduct({
        productData
      }, {
        productLine,
        productLevel: newProductLevel
      });
      if (newSkuId) {
        const newProduct = getProductForSkuId({
          productData
        }, {
          skuId: newSkuId
        });
        if (newProduct) {
          originalApiNames.push(newProduct.apiName);
        }
      }
    }
    setIsLoading(true);
    const getProductBundlesCall = isAppAuth ? getProductBundles : getPublicProductBundles;
    getProductBundlesCall(originalApiNames).then(response => {
      const newProducts = response.products;
      const newProductConfigurations = newProducts.map(({
        skuId
      }) => {
        //maintain original quantities for any preserved skus
        const originalConfig = productConfigurations.find(config => config.skuId === skuId);
        return createProductConfig({
          skuId,
          quantity: originalConfig ? originalConfig.quantity : 1
        });
      });
      setProductConfigurations(newProductConfigurations);
    }).catch(err => {
      // Sentry logged in bundles API call
      console.error(err);
    }).finally(() => {
      setIsLoading(false);
    });
  }, [currencyCode, gates, isAppAuth, ownedProductLevel, productConfigurations, productData, productInventory, productLine, screen, selectedProductLevel, setIsLoading, setProductConfigurations, trackingProductNamesByProductLevelMap, trackingSource]);
  return setProductLevel;
};